import React, { useState, useEffect, useRef, forwardRef, createContext } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useProgress } from '@react-three/drei';
import { WhoIWhyFace } from '../../assets/WhoiwhyFace/Whoiwhy_face_v1';
import { Laikka } from '../../assets/Laikka/Laikka';
import { Insta } from '../../assets/Insta/Insta'
import { Horse } from '../../assets/Horse/Horse_noanim'
import { Ocean } from '../../assets/Ocean/Ocean'
import { Heart } from '../../assets/Heart/Heart'
import { At } from '../../assets/At/At'
import { Flash } from '../../assets/Flash/Flash';
import { Crtttt } from '../../assets/CRT/Crtttt';
import { Djjjj } from '../../assets/DJ/Djjjj';
import { Minimalspider } from '../../assets/Spider/Minimalspider';
import { Warum } from '../../assets/Warum/Warum';
import { Itchi } from '../../assets/Itchi/itchi';
import { Type } from '../../assets/Type/Type';
import ClickableMeshWithTooltip from '../ClickableMeshWithTooltip/ClickableMeshWithTooltip';
import Strobe from '../Strobe/Strobe';
import { useWindowSize } from '@uidotdev/usehooks';
import { tooltipContext } from './TooltipContext';

import './ThreeDContainer.css';

const openLink = (href) => window.open(href, '_blank', 'noopener,noreferrer');

const mapRange = (val, min_in, max_in, min_out, max_out) => {
    if (val <= min_in) return min_out;
    if (val >= max_in) return max_out;
    const ratio_in = (val - min_in) / (max_in - min_in);
    return min_out + ratio_in * (max_out - min_out);
}
const uniVec = val => [val, val, val];

const calcDesktopScale = ({ width, height }) => {
    // 1 for 16/9 and more wide
    // interpolate in [MIN_SCALE, 1] for [1, 16/9[
    const MIN_SCALE = .769;
    const aspectRatio = width / height;
    if (aspectRatio >= 16 / 9) return 1;
    return mapRange(aspectRatio, 1, 16 / 9, MIN_SCALE, 1);
}

const calcMobileScale = ({ width, height }) => {
    // this one is a little funny
    // it is used completely different from desktop scale
    // use it as it has been and trust
    const aspectRatio = width / height;
    if (aspectRatio >= 16 / 9) return 1;
    return mapRange(aspectRatio, 375./960., 1, .5, 1);
}

const isMobileContext = createContext();

const DEFAULT_WIN_DIMS = {width: 1080, height: 1920};

const ThreeDContainer = ({ heightPercent = 100 }) => {

    // window dims hook
    const windowDims = useWindowSize();

    const [windowDimensions, setWindowDimensions] = useState(DEFAULT_WIN_DIMS);
    const [isMobile, setIsMobile] = useState(false);
    const [desktopScale, setDesktopScale] = useState(calcDesktopScale(windowDimensions));
    const [mobileScale, setMobileScale] = useState(.5);
    const [strobe, setStrobe] = useState(false);
    const [aboutOpen, setAboutOpen] = useState(false);

    // loading overlay: stays up until every queued GLTF has finished loading
    const { active, loaded, total } = useProgress();
    const [assetsReady, setAssetsReady] = useState(false);
    useEffect(() => {
        if (!assetsReady && total > 0 && loaded >= total && !active) {
            setAssetsReady(true);
        }
    }, [active, loaded, total, assetsReady]);

    // cycles "loading." -> "loading.." -> "loading..." while the overlay is up
    const [loadingDots, setLoadingDots] = useState(1);
    useEffect(() => {
        if (assetsReady) return;
        const interval = setInterval(() => {
            setLoadingDots((d) => (d % 3) + 1);
        }, 500);
        return () => clearInterval(interval);
    }, [assetsReady]);

    // tooltip content + position (position is set directly on the DOM node to avoid a re-render per mousemove)
    const [tooltipContent, setTooltipContent] = useState(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        const TOOLTIP_OFFSET = 12;
        const handleMouseMove = (event) => {
            if (!tooltipRef.current) return;
            const { clientX, clientY } = event;
            tooltipRef.current.style.left = `${clientX}px`;
            tooltipRef.current.style.top = `${clientY}px`;

            // keep the tooltip pointing inward: it sits on whichever side of the
            // cursor faces the screen center, so it never runs off the edge
            const isLeftHalf = clientX < window.innerWidth / 2;
            const isTopHalf = clientY < window.innerHeight / 2;
            const xPercent = isLeftHalf ? 0 : -100;
            const yPercent = isTopHalf ? 0 : -100;
            const xOffset = isLeftHalf ? TOOLTIP_OFFSET : -TOOLTIP_OFFSET;
            const yOffset = isTopHalf ? TOOLTIP_OFFSET : -TOOLTIP_OFFSET;
            tooltipRef.current.style.transform =
                `translate(calc(${xPercent}% + ${xOffset}px), calc(${yPercent}% + ${yOffset}px))`;
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // update window dims state using hook
    useEffect(() => {
        if(!windowDims.width || !windowDims.height){
            setWindowDimensions(DEFAULT_WIN_DIMS);
            return;
        }
        setWindowDimensions(windowDims);
    }, [windowDims]);

    // update desktop scale
    useEffect(() => {
        if(isMobile) setMobileScale(calcMobileScale(windowDimensions));
        else setDesktopScale(calcDesktopScale(windowDimensions));
    }, [windowDimensions, isMobile]);

    // update isMobile (aspect ratios of 1 and less)
    useEffect(() => {
        const aspectRatio = windowDimensions.width / windowDimensions.height;
        setIsMobile(aspectRatio < 1);
    }, [windowDimensions])

    // lookat target
    const Target = forwardRef((props, ref) => {

        const [pos, setPos] = useState([10, 0, -5])

        // mouse tracking
        const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
        useEffect(() => {
            const handleMouseMove = (event) => {
                setMousePosition({ x: event.clientX, y: event.clientY });
            };

            window.addEventListener('mousemove', handleMouseMove);

            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
            };
        }, []);
        // Convert mouse position to range [0, 1]
        const x = (mousePosition.x / windowDimensions.width);
        const y = 1 - (mousePosition.y / windowDimensions.height);

        //Update the target position of the target based on mouse coordinates
        useFrame(() => {
            const newY = -5 + y * 10;
            const newZ = 5 - x * 10;
            setPos([10, newY, newZ])
        });

        return (
            <mesh ref={ref} position={pos} ></mesh>
        );
    });
    const targetRef = useRef();

    return (
        <isMobileContext.Provider value={{isMobile}}>
            <tooltipContext.Provider value={{ setTooltip: setTooltipContent }}>
            <div
                className='threeDContainer'
                style={{
                    height: `${heightPercent}vh`,
                }}
            >
                {/* Loading overlay */}
                {!assetsReady && (
                    <div className='threeDLoadingOverlay'>
                        <div className='threeDLoadingTitle'>?? WHO I WHY ??</div>
                        <div className='threeDLoadingText'>{'loading' + '.'.repeat(loadingDots)}</div>
                        <div className='threeDLoadingSpinner'>
                            ᓚᘏᗢ
                        </div>
                    </div>
                )}

                {/* Tooltip */}
                <div
                    ref={tooltipRef}
                    className='threeDTooltip'
                    style={{ display: tooltipContent ? 'block' : 'none' }}
                >
                    {tooltipContent && (
                        <>
                            <div className='threeDTooltipHeadline'>{tooltipContent.headline}</div>
                            {tooltipContent.body && (
                                <div className='threeDTooltipBody'>{tooltipContent.body}</div>
                            )}
                        </>
                    )}
                </div>

                {/* About Pop-Up */}
                {aboutOpen && (
                    <div className='threeDAboutOverlay' onClick={() => setAboutOpen(false)}>
                        <div className='threeDAboutPopup' onClick={(e) => e.stopPropagation()}>
                            <button
                                className='threeDAboutClose'
                                onClick={() => setAboutOpen(false)}
                                aria-label='close'
                            >
                                ×
                            </button>
                            <div className='threeDTooltipHeadline'>mo wunderwald / who i why</div>
                            <div className='threeDTooltipBody'>
                                Mo Wunderwald (they/them) is a musician and media artist based in Vienna, AT, whose work centres on music and spans sound art, performance and digital animation.
                                Mo began writing and performing music as a teenager, drumming in emo bands before moving toward electronic sounds. As a young adult, they became active in the techno scene as a DJ and producer, a culture that has deeply shaped their artistic practice, and later worked in pop formations, which led them into videography and photography. After moving to Vienna to study computer science with a focus on digital media, Mo developed a strong interest in digital art and went on to study at the class for digital arts at the University of Applied Arts Vienna. Their work since has ranged from the VR piece "the ocean is a black/blue/grey/brown/black ocean" (Parallel art fair, 2022) to the animated short "no horse zone" (Best Sound, Best Austrian Animation Festival 2024) and the immersive exhibition "Leech Seed" (2025, with longtime collaborator Rage). Since 2020, Mo has also led the music project Laikka, touring internationally, earning nominations at the Amadeus Music Awards and Shape+, and creating a commissioned piece for Ö1's Musikprotokoll.
                            </div>
                        </div>
                    </div>
                )}
                <Canvas
                    camera={{ position: [8.4, 0, 0], fov: 69 }}
                    style={{
                        backgroundColor: '#000000',
                        width: '100vw',
                        height: `${heightPercent}vh`,
                    }}
                >
                    <group scale={isMobile ? .5 : desktopScale}>

                        <ambientLight intensity={0.0242} />
                        {/* strobes */}
                        {
                            strobe && (<>
                                <Strobe 
                                    position={[10, 10, 10]} 
                                    baseIntensity={.42} 
                                    maxIntensity={6.9} 
                                />
                                <Strobe 
                                    position={[20, -10, -10]} 
                                    baseIntensity={.21} 
                                    maxIntensity={5.42} 
                                />
                            </>)
                        }
                        {/* no strobe zone */}
                        {
                            !strobe && (<>
                                <pointLight 
                                    position={isMobile 
                                        ? [12.1, -4.2, 4.2]
                                        : [12.1, -4.2, 4.2]
                                    } 
                                    intensity={.69} 
                                />
                            </>)
                        }
                        {/* base strobe */}
                        {
                            !strobe && (
                                <Strobe 
                                    position={[-42, 0, 0]} 
                                    baseIntensity={2.42} 
                                    maxIntensity={2.69} 
                                />
                            )
                        }

                        <group className={'type'}>
                            <Type
                                position={isMobile
                                    ? [0, 9, 0]
                                    : [-42, 0, 0]
                                }
                                scale={isMobile
                                    ? uniVec(2.44  * mobileScale*1.7)
                                    : uniVec(desktopScale * desktopScale * 42)
                                }
                            />
                        </group>

                        <group className={'Face'}>
                            <WhoIWhyFace 
                                position={isMobile
                                    ? [0, -8.88, 0]
                                    : [0, -.69, 0]
                                } 
                                scale={isMobile
                                    ? uniVec(.42)
                                    : uniVec(.84)
                                } 
                                target={targetRef} 
                            />
                            <Target ref={targetRef} />
                        </group>

                        <group className={'Links'}>

                            {/* Heart // FF Animination */}
                            <ClickableMeshWithTooltip 
                                position={isMobile
                                    ? [0, -2.25, -2.2 * mobileScale*2]
                                    : [0, 1.22, -5]
                                } 
                                scale={isMobile
                                    ? uniVec(5)
                                    : uniVec(5.2)
                                } 
                                onclick={() => openLink('https://youtu.be/GIJdxBY6SCg')}
                                tooltipHeadline='in the clurb (nothing really matters...)'
                                tooltipBody='animation for queer collectives fette feten (vienna) and kink (budapest) as promo material and world building for their common event. created by generating meshes from my hand drawings.'
                            >
                                <Heart position={[0, 0, 0]} scale={[.1, .1, .1]} />
                            </ClickableMeshWithTooltip>

                            {/* No Horse Zone */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, 5.3, 2.1 * mobileScale*2]
                                    : [0, 3.3, 3.55]
                                } 
                                scale={isMobile
                                    ? uniVec(.555)
                                    : uniVec(.396)
                                } 
                                onclick={() => openLink('https://youtu.be/ZhJY3AXj824')}
                                tooltipHeadline='no horse zone'
                                tooltipBody='animated short movie. an unsettling series of events watched through sore, restlessly dissociating eyes while ghostly kin is lurking in the foreshadows. '
                            >
                                <Horse position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* Leech Seed*/}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, 4.55, -1.5 * mobileScale*2]
                                    : [0, -1.3, 6.1]
                                } 
                                scale={isMobile
                                    ? uniVec(1)
                                    : uniVec(1)
                                } 
                                onclick={() => openLink('https://www.are.na/mo-wunderwald/leech-seed-2025')}
                                tooltipHeadline='leech seed'
                                tooltipBody='immersive exhibition with rage at seesaal wien, 2025. combining a projection-mapped bio sculpture, a directional sound installation and self-decomposing video works. exhibition was part of BAAF best austrian animation festival.'
                            >
                                <Minimalspider position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* Itchi */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, -2.25, 0.9 * mobileScale*2]
                                    : [0, 3.8, 7.1]
                                } 
                                scale={isMobile
                                    ? uniVec(.22)
                                    : uniVec(.15)
                                } 
                                rotation={[0, 90, 0]}
                                onclick={() => openLink('https://fabrique.at/artist/itchi/')}
                                tooltipHeadline='itchi'
                                tooltipBody='we are ITCHI we are a BAND'
                            >
                                <Itchi position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>


                            {/* Laikka */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, 1.1, 2.7 * mobileScale*2]
                                    : [0, 4.4, -1.169]
                                } 
                                scale={isMobile
                                    ? uniVec(1.33)
                                    : uniVec(0.9)
                                } 
                                onclick={() => openLink('https://www.whereislaikka.com')}
                                tooltipHeadline='laikka'
                                tooltipBody='my baaaand <3'
                            >
                                <Laikka position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* DJ */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, -4.55, 2.66 * mobileScale*2]
                                    : [0, 3.99, -7.169]
                                } 
                                scale={isMobile
                                    ? uniVec(1.1)
                                    : uniVec(0.5)
                                } 
                                rotation={[-90, 0, -90]}
                                onclick={() => openLink('https://soundcloud.com/who-i-why/schere3000-25-04')}
                                tooltipHeadline="I'M A DJ!!"
                                tooltipBody='LISTEN TO MY MIX'
                            >
                                <Djjjj position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* Graz Soundperformance */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, 2.11, -1.99 * mobileScale*2]
                                    : [0, -.2, -7.169]
                                } 
                                scale={isMobile
                                    ? uniVec(.92)
                                    : uniVec(0.6)
                                } 
                                rotation={[0, -70, 20]}
                                onclick={() => openLink('https://archiv.steirischerherbst.at/en/projects/29646/zwischen-den-wellen-between-the-waves')}
                                tooltipHeadline="between the waves"
                                tooltipBody='sound performance commisioned by ORF Musikprotokoll 2025 and performed at Dom im Berg Graz. created for a mixed audience of people with and without hearing impairments as a collaboration of Laikka (me & alexander hackl) and david obermair.'
                            >
                                <Crtttt position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* Ocean */}
                            <ClickableMeshWithTooltip 
                                position={isMobile 
                                    ? [0, -4.99, -2.7 * mobileScale*2]
                                    : [0, -1.42, -4.2]
                                } 
                                scale={isMobile
                                    ? uniVec(.33)
                                    : uniVec(.33)
                                } 
                                onclick={() => openLink('https://vimeo.com/844684360')}
                                tooltipHeadline='the ocean is a black/blue/grey/brown/black ocean'
                                tooltipBody='VR experience exploring early generative AI through a melancholic, dystopian lens. made with rage and alexander hackl.'
                            >
                                <Ocean position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* Mail */}
                            <ClickableMeshWithTooltip 
                                position={isMobile
                                    ? [0, -8.88, 3.33 * mobileScale*2]
                                    : [0, -4.2, 0]
                                } 
                                scale={isMobile
                                    ? uniVec(5)
                                    : uniVec(4.7)
                                } 
                                onclick={() => openLink('mailto:mo@whoiwhy.com')}
                                tooltipHeadline='e-mail'
                                tooltipBody='get in touch! ฅ^>⩊<^ ฅ'
                            >
                                <At position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>
                            
                            {/* Insta */}
                            <ClickableMeshWithTooltip 
                                position={isMobile
                                    ? [0, -8.88, -3.33 * mobileScale*2]
                                    : [0, -4.2, 4.69]
                                } 
                                scale={isMobile
                                    ? uniVec(1.69)
                                    : uniVec(1.4)
                                } 
                                onclick={() => openLink('https://www.instagram.com/who.i.why')}
                                tooltipHeadline='instagram'
                            >
                                <Insta position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>

                            {/* About */}
                            <ClickableMeshWithTooltip 
                                position={isMobile
                                    ? [0, -5.22, 0 * mobileScale*2]
                                    : [0, -4.2, -4.5]
                                } 
                                scale={isMobile
                                    ? uniVec(2.7)
                                    : uniVec(2.2)
                                } 
                                rotation={[0, 90, 0]}
                                onclick={() => setAboutOpen(true)}
                                tooltipHeadline='about meeeeeeeee'
                                tooltipBody='☝️(˶⎚⤙⎚˶)'
                            >
                                <Warum position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>




                            {/* Strobe */}
                            {!isMobile && <ClickableMeshWithTooltip 
                                position={[0, -3.33, -7.89]} 
                                scale={uniVec(.2)} 
                                onclick={() => setStrobe(!strobe)}
                                tooltipHeadline='strobe'
                                tooltipBody='click if you dare'
                            >
                                <Flash position={[0, 0, 0]} scale={[1, 1, 1]} />
                            </ClickableMeshWithTooltip>}
                        </group>

                    </group>
                </Canvas>
            </div>
            </tooltipContext.Provider>
        </isMobileContext.Provider>
    );
};



export {ThreeDContainer, isMobileContext, tooltipContext};